/**
 * A ship scene's world (ADR 0033): brad-airship's `airship-world.js`
 * `buildAirship` and the per-frame wiring of `airship-main.js`,
 * `airship-focus.js` (`configureAirshipLighting`, `applyAirshipGrade`),
 * `airship-time.js` (`installTimeOfDay` `update`), `airship-flight.js` and
 * `airship-air.js`, on the scene record (`area.ts`) and the room grid
 * (`@ab/scene` `shipLayout`), drawn by the scene view (`scene/view.ts`).
 *
 * Build, once per ship scene:
 * - the walkable surfaces the exterior does not lay (the cliff shelf clipped
 *   at the jetty, its heel, the jetty's planks, the gangplank), the jetty's
 *   rails and cantilever braces (part `mooring`, which falls astern in flight);
 * - every placement through the ship's prefabs (`prefabs.ts`: the exterior,
 *   the mooring post, the cargo) and every room of the deck through its
 *   type's level's prefab, turned about its centre;
 * - the flush hatches at the interior's companionways (every room's door
 *   that is a passage to the deck, both rooms' alike; never the doorway the
 *   two rooms share: the prototype's interior had none there);
 * - batching: every static mesh is baked to world space and merged by
 *   material (one draw each); on the deck the plain tint materials batch into
 *   one vertex-coloured base with their colour baked (`DECK_PALETTE`, the
 *   GPU pass's cut), the exterior's tint-only variants into their base;
 *   named parts (the mooring, the set and furled sails, the ensigns, the
 *   laundry) batch per part and material and fade as one; a room's roof
 *   (`reveal`) batches per room and material and dissolves while the
 *   traveler is inside the room; dynamic meshes (an incubator's egg) stay;
 * - the lamp sources (every night-glow glass, lit skylights) located before
 *   batching, baked into the deck's lamp light; the static irradiance volume
 *   (below deck from the rooms, on deck over the built bounds); the cloud
 *   shadows on the deck's materials;
 * - the sky, the weather, the motes, the flight's streaks and smoke, the
 *   clutter cards, the hatch displays at the hatch spots. The crew are the
 *   scene's residents, drawn by the scene view as residents are (proxies
 *   until their sheets exist: ADR 0033 D8).
 *
 * `frame` then runs the time of day, the wind and flight, the reveals, the
 * cards and the lens's ship uniforms, and says when the shadow map must be
 * drawn again (a part appeared or went, the sun moved).
 */
import type { Scene } from '@ab/content-schema';
import type { ShipRoomLayout, WalkMap } from '@ab/scene';
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Lens } from '../lens.ts';
import { addShaderHook, copyShaderHooks } from '../material-hooks.ts';
import type { Look } from '../scene/look.ts';
import { shipPrefab } from '../scene/catalog.ts';
import { type RoomBuild, type ShipArea, shipArea } from './area.ts';
import { type Clutter, type ClutterItem, createClutter } from './clutter.ts';
import { shipLook } from './constants.ts';
import { type HatchDisplay, type HatchPhase, createHatchDisplay } from './creatures.ts';
import { type ShipMotion, createShipMotion } from './flight.ts';
import { cloudShadowHook, lampHook, radianceHook, revealHook } from './hooks.ts';
import { type ShipKit, box, createShipKit, flags, isMesh, primitive, tag, tagsOf } from './kit.ts';
import { type LampSource, type VolumeBake, bakeLampLight, bakeShipRadiance } from './lamps.ts';
import { type Motes, createMotes } from './motes.ts';
import { buildPortalHatch } from './room-kit.ts';
import { type ShipSky, createShipSky } from './sky.ts';
import type { DynamicMesh } from './types.ts';
import { type Weather, type WeatherNoise, buildWeatherNoise, createWeather } from './weather.ts';

/** A room's roof reveal: its rectangle in three's axes, its fade (0 shown, 1 gone) and the fade it heads for. */
export type Reveal = { readonly room: string; readonly fade: THREE.IUniform<number>; target: number; readonly hx: number; readonly hz: number; readonly cx: number; readonly cz: number };

export type ShipFrame = {
  readonly dt: number;
  /** The scene's clock (motion-scaled), seconds. */
  readonly time: number;
  /** The event clock (real time, never motion-scaled): an event's sequence (the hatch) runs on it. */
  readonly eventTime: number;
  readonly look: Look;
  readonly medium: boolean;
  readonly camera: THREE.PerspectiveCamera;
  readonly viewportHeight: number;
  /** The traveler's feet in three's axes. */
  readonly player: THREE.Vector3;
  /** 0 moored, 1 under way. */
  readonly flight: number;
  /** The motion strength (0 under reduced motion: no wind, no motes, cards hold). */
  readonly motion: number;
  readonly lens: Lens | null;
};

export type ShipFrameResult = {
  /** The shadow map must be drawn again (a part changed, the sun moved). */
  readonly shadows: boolean;
  /** The sun's intensity factor (cloud shadow at the ship). */
  readonly sunScale: number;
  /** Something animates by itself (Medium draws anyway; Off asks for frames while a blend or a hatch runs). */
  readonly animating: boolean;
};

export type ShipWorld = {
  readonly root: THREE.Group;
  readonly kit: ShipKit;
  readonly area: ShipArea;
  /** The ship and jetty's frame (the overview's and the deck radiance's), three's axes; null below deck. */
  readonly bounds: THREE.Box3 | null;
  readonly merged: number;
  readonly reveals: readonly Reveal[];
  /** Each incubator's egg mesh by interactable id. */
  readonly eggs: ReadonlyMap<string, DynamicMesh>;
  readonly hatches: ReadonlyMap<string, HatchDisplay>;
  readonly clutter: Clutter | null;
  readonly motes: Motes | null;
  readonly sky: ShipSky | null;
  readonly motion: ShipMotion | null;
  /** The scene's atlases by texture slot (`clutter`, `hatch`): given when they land. */
  setAtlas(slot: string, texture: THREE.Texture): void;
  /** Shows an incubator's egg (incubating or ready) or hides it. */
  setEgg(id: string, state: 'empty' | 'incubating' | 'ready'): void;
  frame(f: ShipFrame): ShipFrameResult;
  /** The Off tier's frees (the sky's and motes' programs; the deck keeps its baked textures). */
  setTier(medium: boolean): void;
  dispose(): void;
};

type OffSky = { readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>; apply(below: THREE.Color, horizon: THREE.Color, zenith: THREE.Color): void; release(): void; dispose(): void };

/**
 * Off's sky (the tier has no sky shader): a screen-filling quad behind everything, a vertical gradient from the sea's
 * far tone at the bottom through the horizon (at `shipLook.offSky.horizonAt` of the height) to the zenith at the top.
 * Hidden at Medium, where the sky shader draws; its buffers and program are freed when Medium takes over.
 */
const createOffSky = (): OffSky => {
  const geometry = new THREE.PlaneGeometry(2, 2);
  const material = new THREE.ShaderMaterial({
    name: 'airship-off-sky',
    uniforms: { below: { value: new THREE.Color() }, horizon: { value: new THREE.Color() }, zenith: { value: new THREE.Color() }, horizonAt: { value: shipLook.offSky.horizonAt } },
    vertexShader: 'varying float vY;void main(){vY=position.y*.5+.5;gl_Position=vec4(position.xy,1.,1.);}',
    fragmentShader: `uniform vec3 below;uniform vec3 horizon;uniform vec3 zenith;uniform float horizonAt;varying float vY;
      void main(){vec3 c=vY<horizonAt?mix(below,horizon,vY/horizonAt):mix(horizon,zenith,(vY-horizonAt)/(1.-horizonAt));gl_FragColor=vec4(c,1.);
      #include <colorspace_fragment>
      }`,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'Off sky';
  mesh.frustumCulled = false;
  mesh.renderOrder = -1000;
  mesh.visible = false;
  const u = material.uniforms;
  return {
    mesh,
    apply(below, horizon, zenith) {
      (u['below'] as THREE.IUniform<THREE.Color>).value.copy(below);
      (u['horizon'] as THREE.IUniform<THREE.Color>).value.copy(horizon);
      (u['zenith'] as THREE.IUniform<THREE.Color>).value.copy(zenith);
    },
    release() {
      geometry.dispose();
      material.dispose();
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
};

/** The colours an incubator's egg shows by state (`airship-main.js` `stateColors`). */
const eggColors = { incubating: '#cc9575', ready: '#dcc078' } as const;

type Bucket = { readonly material: THREE.Material; readonly part: string | null; readonly geometries: THREE.BufferGeometry[] };

/** Merges geometries whose attribute sets may differ: each gets every attribute of the bucket, zero-filled where it had none. */
const mergeAll = (geometries: readonly THREE.BufferGeometry[]): THREE.BufferGeometry => {
  const sizes = new Map<string, number>();
  for (const g of geometries) for (const [name, a] of Object.entries(g.attributes)) if (!sizes.has(name)) sizes.set(name, a.itemSize);
  for (const g of geometries) {
    const n = g.getAttribute('position').count;
    for (const [name, size] of sizes) if (!g.hasAttribute(name)) g.setAttribute(name, new THREE.BufferAttribute(new Float32Array(n * size), size));
  }
  const merged = mergeGeometries([...geometries], false);
  if (merged === null) throw new Error('ship: static geometry merge failed');
  return merged;
};

/** Bakes a mesh's geometry into world space, unindexed, with a zero normal turned up (bloom's NaN rule, `batch.ts`). */
const baked = (o: THREE.Mesh): THREE.BufferGeometry => {
  const g = o.geometry.clone();
  const flat = g.index !== null ? g.toNonIndexed() : g;
  if (flat !== g) g.dispose();
  flat.applyMatrix4(o.matrixWorld);
  const normal = flat.getAttribute('normal') as THREE.BufferAttribute | undefined;
  if (normal !== undefined) for (let i = 0; i < normal.count; i += 1) if (normal.getX(i) === 0 && normal.getY(i) === 0 && normal.getZ(i) === 0) normal.setXYZ(i, 0, 1, 0);
  return flat;
};

/** A surface's flat shape (a polygon, or its rect), clipped west of `clipEdge` when given (`surfaceGeometry`). */
const surfaceGeometry = (s: ShipArea['surfaces'][number], clipEdge: number | null): THREE.BufferGeometry => {
  const polygon: readonly (readonly [number, number])[] = s.polygon ?? [
    [s.x0, s.z0],
    [s.x1, s.z0],
    [s.x1, s.z1],
    [s.x0, s.z1],
  ];
  const points: (readonly [number, number])[] = [];
  if (clipEdge === null) points.push(...polygon);
  else {
    for (let i = 0; i < polygon.length; i += 1) {
      const a = polygon[i];
      const b = polygon[(i + 1) % polygon.length];
      if (a === undefined || b === undefined) continue;
      if (a[0] <= clipEdge) points.push(a);
      if (a[0] <= clipEdge !== b[0] <= clipEdge) {
        const t = (clipEdge - a[0]) / (b[0] - a[0]);
        points.push([clipEdge, a[1] + (b[1] - a[1]) * t]);
      }
    }
  }
  const shape = new THREE.Shape(points.map(([x, z]) => new THREE.Vector2(x, -z)));
  const g = new THREE.ShapeGeometry(shape);
  g.rotateX(-Math.PI / 2);
  g.translate(0, s.y, 0);
  return tag(g);
};

export type ShipWorldOptions = {
  readonly scene: Scene;
  /** The rooms drawn: every room of the scene's deck (a room scene draws its neighbours too, as the prototype's interior did). */
  readonly rooms: readonly ShipRoomLayout[];
  readonly map: WalkMap;
};

export const buildShipWorld = ({ scene, rooms, map }: ShipWorldOptions): ShipWorld => {
  const area = shipArea(scene, rooms);
  const deck = area.id === 'deck';
  const kit = createShipKit({ interior: !deck });
  const root = new THREE.Group();
  root.name = `ship:${scene.id}`;
  const staging = new THREE.Group();
  const m = kit.m;
  const material = (key: string): THREE.MeshStandardMaterial => kit.get(key);
  // Below deck the volume is the rooms' alone (`createAirshipRadiance`), baked before anything is built so every
  // material that samples it reads it from its first frame.
  let radianceBake: VolumeBake | null = null;
  if (!deck && kit.radiance !== null) {
    radianceBake = bakeShipRadiance(area, null);
    kit.radiance.hdRadiance.value = radianceBake.texture;
    kit.radiance.hdRadianceMin.value.copy(radianceBake.min);
    kit.radiance.hdRadianceSpan.value.copy(radianceBake.span);
  }
  let part: string | null = null;
  const tagPart = <T extends THREE.Object3D>(o: T): T => {
    if (part !== null) tagsOf(o).part = part;
    return o;
  };
  const block = (key: string, x: number, y: number, z: number, w: number, h: number, d: number): THREE.Mesh => tagPart(box(staging, material(key), x, y, z, w, h, d));
  const add = (g: THREE.BufferGeometry, key: string, x = 0, y = 0, z = 0): THREE.Mesh => tagPart(primitive(staging, material(key), g, x, y, z));
  const cylinder = (key: string, x: number, y: number, z: number, r: number, h: number): THREE.Mesh => add(new THREE.CylinderGeometry(r, r, h, 8), key, x, y, z);

  // Walkable surfaces the exterior does not lay: the cliff shelf (clipped at the jetty) and its heel, the jetty's planks, the gangplank.
  for (const s of area.surfaces) {
    part = s.id.startsWith('ship') ? null : 'mooring';
    if (s.id.startsWith('ship_deck')) continue;
    if (s.id === 'cliff_threshold') {
      const mooring = area.surfaces.find((q) => q.id === 'mooring');
      add(surfaceGeometry(s, mooring?.x0 ?? null), 'cliff');
      // The buried heel foundation is a simple grey-box mass.
      block('cliff', 6.15, -3.1, 0, 1.3, 6.1, 7.3);
    } else if (s.id === 'mooring') {
      // Higuera's floor plank dimensions, top at y 0; no second plane at y 0.
      block('timber', (s.x0 + s.x1) / 2, -0.205, (s.z0 + s.z1) / 2, s.x1 - s.x0, 0.19, s.z1 - s.z0);
      for (let z = s.z0 + 0.15; z < s.z1; z += 0.32) block('timber', (s.x0 + s.x1) / 2, -0.055, z, s.x1 - s.x0, 0.11, 0.3);
    } else {
      const thickness = s.id === 'gangplank' ? 0.22 : 0.34;
      block('floor', (s.x0 + s.x1) / 2, s.y - thickness / 2, (s.z0 + s.z1) / 2, s.x1 - s.x0, thickness, s.z1 - s.z0);
    }
  }
  part = null;
  // The rooms through their level's prefab, each turned about its centre (clockwise seen from above).
  const dynamic: DynamicMesh[] = [];
  const builds: RoomBuild[] = [...area.rooms];
  for (const room of builds) {
    const prefab = shipPrefab(room.prefab as Parameters<typeof shipPrefab>[0]);
    if (prefab === null) throw new Error(`${scene.id}: room ${room.id}'s prefab ${room.prefab} is not a ship prefab`);
    const built = prefab.build(kit, {}, { area, room });
    built.root.position.set(room.center.x, room.y, room.center.z);
    built.root.rotation.y = (-room.rotation * Math.PI) / 180;
    staging.add(built.root);
    dynamic.push(...(built.dynamic ?? []));
  }
  // The jetty's rails (the deck's own rails are the exterior's balustrade).
  part = 'mooring';
  for (const c of area.colliders.filter((q) => q.kind === 'rail' && !q.id.startsWith('ship'))) {
    const w = c.x1 - c.x0;
    const d = c.z1 - c.z0;
    const horizontal = w > d;
    const len = Math.max(w, d);
    const count = Math.ceil(len / 2.5);
    for (let i = 0; i <= count; i += 1) {
      const t = i / count;
      cylinder('timber', horizontal ? c.x0 + w * t : (c.x0 + c.x1) / 2, 0.45, horizontal ? (c.z0 + c.z1) / 2 : c.z0 + d * t, 0.08, 0.9);
    }
    block('timber', (c.x0 + c.x1) / 2, 0.85, (c.z0 + c.z1) / 2, horizontal ? w : 0.1, 0.1, horizontal ? 0.1 : d);
  }
  // Higuera's cantilever brace coordinates and dimensions under the jetty.
  if (deck) {
    const r = shipLook.braces.rails;
    for (const z of r.zs) block('timber', r.x, r.y, z, r.length, r.height, r.width);
    for (const [heelZ, z] of [
      [-3.6, -4.4],
      [3.35, 5.4],
    ] as const) {
      const start = new THREE.Vector3(6.2, -4.6, heelZ);
      const end = new THREE.Vector3(23, -0.36, z);
      const v = end.clone().sub(start);
      const g = new THREE.BoxGeometry(0.42, v.length(), 0.42);
      g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize()));
      const center = start.clone().add(end).multiplyScalar(0.5);
      add(g, 'timber', center.x, center.y, center.z);
      block('trim', 8.6, -3.99, heelZ + (z - heelZ) * (2.4 / 16.8), 0.65, 0.65, 0.7);
      for (const x of [14, 18, 22]) {
        const t = (x - 6.2) / 16.8;
        const braceY = -4.6 + t * 4.24;
        const braceZ = heelZ + (z - heelZ) * t;
        block('timber', x, (braceY - 0.3) / 2, braceZ, 0.22, -0.3 - braceY, 0.24);
        block('timber', x, -0.3, (braceZ + z) / 2, 0.26, 0.24, Math.abs(z - braceZ) + 0.35);
      }
    }
  }
  part = null;
  // Placements: the exterior (a set piece at the origin), the mooring post (part mooring) and the cargo.
  const clutterItems: ClutterItem[] = [];
  for (const p of scene.placements) {
    if (p.prefab === 'ship_clutter') {
      const q = p.params ?? {};
      const str = (k: string): string | undefined => {
        const v = q[k];
        return typeof v === 'string' ? v : undefined;
      };
      const n = (k: string): number | undefined => {
        const v = q[k];
        return typeof v === 'number' ? v : undefined;
      };
      const b = (k: string): boolean => q[k] === true;
      const sprite = str('sprite');
      if (sprite === undefined) continue;
      const lift = n('lift');
      const angle = n('angle');
      const scale = n('scale');
      clutterItems.push({ sprite, x: p.x, z: -p.y, depth: n('depth') ?? 0, flip: b('flip'), jetty: b('jetty'), decal: b('decal'), ...(lift === undefined ? {} : { lift }), ...(angle === undefined ? {} : { angle }), ...(scale === undefined ? {} : { scale }) });
      continue;
    }
    const prefab = shipPrefab(p.prefab);
    if (prefab === null) throw new Error(`${scene.id}: ${p.prefab} is not a ship prefab`);
    const built = prefab.build(kit, p.params ?? {}, { area, room: null });
    built.root.position.set(p.x, p.h ?? 0, -p.y);
    built.root.rotation.y = THREE.MathUtils.degToRad(p.rotation ?? 0);
    staging.add(built.root);
  }
  // Below deck: the flush hatch at each room's companionway (the deck's are the exterior's; a doorway has none).
  if (!deck) for (const p of area.hatches) buildPortalHatch(staging, kit, p);
  if (!deck) {
    const i = shipLook.interior;
    material('cream').color.set(i.cream);
    const lamp = m['hatcheryLamp'];
    if (lamp !== undefined) lamp.emissiveIntensity = i.hatcheryLamp;
    const copper = m['hatcheryCopper'];
    if (copper !== undefined) {
      copper.roughness = i.copperRoughness;
      copper.metalness = i.copperMetalness;
    }
  }
  staging.updateMatrixWorld(true);

  // The lamp bake's sources: every night-glow glass (a window only when it is a lit skylight), located before batching.
  const lamps: LampSource[] = [];
  const s = shipLook.lampSources;
  staging.traverse((o) => {
    if (!(o instanceof THREE.Mesh) || !(o.material instanceof THREE.MeshStandardMaterial)) return;
    if (flags(o.material).nightGlow === undefined) return;
    const bounds = new THREE.Box3().setFromObject(o);
    const c = bounds.getCenter(new THREE.Vector3());
    const window = /windowGlass/.test(o.material.name);
    if (window && !(bounds.max.y > s.windowTop && bounds.min.y > s.windowBottom)) return;
    if (lamps.some((l) => l.position.distanceTo(c) < s.merge)) return;
    // Lamps inside a deckhouse light its small room; kept from flooding the painted trim (they read as neon at night otherwise).
    const inRoom = area.rooms.some((r) => {
      const q = (r.rotation / 90) % 2 === 1;
      const hx = (q ? r.depth : r.width) / 2;
      const hz = (q ? r.width : r.depth) / 2;
      return Math.abs(c.x - r.center.x) < hx + s.roomMargin && Math.abs(c.z - r.center.z) < hz + s.roomMargin;
    });
    lamps.push({ position: c, strength: window ? s.window : inRoom ? s.inRoom : 1, down: window });
  });

  // The deck's plain tint materials batch into one vertex-coloured base with their colours baked (`DECK_PALETTE`).
  const plainTint = new Map<THREE.Material, THREE.MeshStandardMaterial>();
  const deckTint = new Map<THREE.Material, THREE.Color>();
  const owned: { dispose(): void }[] = [];
  if (deck) {
    for (const [key, color] of Object.entries(shipLook.deckPalette)) deckTint.set(material(key), new THREE.Color(color));
    const plain = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 1, vertexColors: true });
    plain.name = 'airship-plainBase';
    kit.finish(plain);
    owned.push(plain);
    for (const key of ['floor', 'timber', 'cream', 'trim', 'cliff', 'blue', 'green', 'red', 'soil', 'warm', 'marker']) plainTint.set(material(key), plain);
    const alternate = m['deckAlternate'];
    if (alternate !== undefined) {
      plainTint.set(alternate, plain);
      deckTint.set(alternate, new THREE.Color(shipLook.deckPalette['floor'] ?? '#ffffff').multiplyScalar(shipLook.deckAlternate));
    }
  }
  const batches = new Map<string, Bucket>();
  const boxes = new Set<string>();
  const revealParts: THREE.Mesh[] = [];
  const dynamicMeshes = new Set(dynamic.map((d) => d.mesh));
  staging.traverse((o) => {
    if (!isMesh(o) || dynamicMeshes.has(o) || tagsOf(o).dynamic === true) return;
    if (!(o.material instanceof THREE.Material)) return;
    if (tagsOf(o).reveal !== undefined) {
      revealParts.push(o);
      return;
    }
    const meshPart = tagsOf(o).part ?? null;
    const source = o.material;
    const g = baked(o);
    // Tint-only materials batch into a shared vertex-coloured base: same texture, one draw, the tint baked in.
    const batchAs = flags(source).batchAs ?? plainTint.get(source);
    if (batchAs !== undefined && source instanceof THREE.MeshStandardMaterial) {
      const c = (meshPart === null ? deckTint.get(source) : undefined) ?? source.color;
      const n = g.getAttribute('position').count;
      const rgb = new Float32Array(n * 3);
      for (let i = 0; i < n; i += 1) rgb.set([c.r, c.g, c.b], i * 3);
      g.setAttribute('color', new THREE.BufferAttribute(rgb, 3));
    }
    const target: THREE.Material = batchAs ?? source;
    for (const [name, size] of flags(target).fillAttributes ?? []) {
      if (!g.hasAttribute(name)) g.setAttribute(name, new THREE.BufferAttribute(new Float32Array(g.getAttribute('position').count * size), size));
    }
    if (o.geometry instanceof THREE.BoxGeometry) {
      g.computeBoundingBox();
      const bb = g.boundingBox;
      if (bb !== null) {
        const key = [source.uuid, ...bb.min.toArray(), ...bb.max.toArray()].map((v) => (typeof v === 'number' ? v.toFixed(5) : v)).join(':');
        if (boxes.has(key)) {
          g.dispose();
          return;
        }
        boxes.add(key);
      }
    }
    const key = `${meshPart ?? ''}|${target.uuid}`;
    let b = batches.get(key);
    if (b === undefined) {
      b = { material: target, part: meshPart, geometries: [] };
      batches.set(key, b);
    }
    b.geometries.push(g);
  });
  let merged = 0;
  const parts = new Map<string, { fade: THREE.IUniform<number>; meshes: THREE.Mesh[] }>();
  /** A clone of a material for a part or a reveal: its flags and hooks kept, the dissolve added. */
  const fadeClone = (source: THREE.Material, fade: THREE.IUniform<number>): THREE.Material => {
    const clone = source.clone();
    Object.assign(flags(clone), flags(source));
    copyShaderHooks(source, clone);
    addShaderHook(clone, revealHook(fade));
    owned.push(clone);
    return clone;
  };
  const place = (mesh: THREE.Mesh, source: THREE.Material): void => {
    const f = flags(source);
    mesh.castShadow = f.noShadow !== true;
    mesh.receiveShadow = true;
    root.add(mesh);
    merged += 1;
  };
  for (const b of batches.values()) {
    const g = mergeAll(b.geometries);
    for (const x of b.geometries) x.dispose();
    if (b.part === null) {
      const mesh = new THREE.Mesh(g, b.material);
      mesh.name = `merged:${b.material.name || b.material.type}`;
      place(mesh, b.material);
      continue;
    }
    let p = parts.get(b.part);
    if (p === undefined) {
      p = { fade: { value: 0 }, meshes: [] };
      parts.set(b.part, p);
    }
    const mesh = new THREE.Mesh(g, fadeClone(b.material, p.fade));
    mesh.name = `part:${b.part}`;
    place(mesh, b.material);
    p.meshes.push(mesh);
  }
  // Dynamic meshes keep their own geometry and material, reparented with their world transform.
  const eggs = new Map<string, DynamicMesh>();
  for (const d of dynamic) {
    const matrix = d.mesh.matrixWorld.clone();
    d.mesh.removeFromParent();
    root.add(d.mesh);
    matrix.decompose(d.mesh.position, d.mesh.quaternion, d.mesh.scale);
    d.mesh.castShadow = true;
    d.mesh.receiveShadow = true;
    if (d.kind === 'incubator') eggs.set(d.id, d);
  }
  // Reveal parts (roofs, the camera-facing upper walls) per room and material, so a room's roof fades as one.
  const revealEntries = new Map<string, { fade: THREE.IUniform<number>; byMaterial: Map<THREE.Material, THREE.BufferGeometry[]> }>();
  for (const o of revealParts) {
    const room = tagsOf(o).reveal ?? '';
    let e = revealEntries.get(room);
    if (e === undefined) {
      e = { fade: { value: 0 }, byMaterial: new Map() };
      revealEntries.set(room, e);
    }
    const source = o.material as THREE.Material;
    const list = e.byMaterial.get(source) ?? [];
    list.push(baked(o));
    e.byMaterial.set(source, list);
  }
  const reveals: Reveal[] = [];
  for (const [room, e] of revealEntries) {
    for (const [source, list] of e.byMaterial) {
      const g = mergeAll(list);
      for (const x of list) x.dispose();
      const mesh = new THREE.Mesh(g, fadeClone(source, e.fade));
      mesh.name = `reveal:${room}`;
      place(mesh, source);
    }
    const r = area.rooms.find((q) => q.id === room);
    if (r === undefined) continue;
    const quarter = (r.rotation / 90) % 2 === 1;
    reveals.push({ room, fade: e.fade, target: 0, hx: (quarter ? r.depth : r.width) / 2 + shipLook.reveal.margin, hz: (quarter ? r.width : r.depth) / 2 + shipLook.reveal.margin, cx: r.center.x, cz: r.center.z });
  }
  // The CPU source geometries go once batched (dynamic meshes keep theirs).
  staging.traverse((o) => {
    if (isMesh(o) && !dynamicMeshes.has(o)) o.geometry.dispose();
  });
  root.updateMatrixWorld(true);

  // The ship and jetty's frame: every built mesh but the mooring, and the jetty's far corners.
  let bounds: THREE.Box3 | null = null;
  if (deck) {
    bounds = new THREE.Box3();
    for (const o of root.children) if (o instanceof THREE.Mesh && !o.name.startsWith('part:mooring')) bounds.expandByObject(o);
    for (const p of shipLook.frameCorners) bounds.expandByPoint(new THREE.Vector3(...p));
  }

  // The clutter cards (on the deck root before the lighting hooks, so they take the sun, lamps and clouds).
  const clutter = deck && clutterItems.length > 0 ? createClutter(kit, clutterItems) : null;
  if (clutter !== null) root.add(clutter.mesh);
  // The hatch displays at the hatch spots (below deck).
  const hatches = new Map<string, HatchDisplay>();
  for (const i of scene.interactables.filter((q) => q.kind === 'hatch_spot')) {
    const d = createHatchDisplay(kit, { x: i.x, y: map.heightAt(i.x, i.y), z: -i.y });
    root.add(d.group);
    hatches.set(i.id, d);
  }

  // The deck's light: the live plain colours, the static radiance over the frame, the cloud shadows and the lamp light on every material.
  let lampBake: VolumeBake | null = null;
  let noise: WeatherNoise | null = null;
  let weather: Weather | null = null;
  let sky: ShipSky | null = null;
  let offSky: OffSky | null = null;
  let motion: ShipMotion | null = null;
  let motes: Motes | null = null;
  if (deck) {
    for (const [key, color] of Object.entries(shipLook.deckPalette)) m[key]?.color.set(color);
    const alternate = m['deckAlternate'];
    if (alternate !== undefined) alternate.color.copy(material('floor').color).multiplyScalar(shipLook.deckAlternate);
    radianceBake = bakeShipRadiance(area, bounds);
    const ru = { hdRadiance: { value: radianceBake.texture as THREE.Data3DTexture | null }, hdRadianceMin: { value: radianceBake.min }, hdRadianceSpan: { value: radianceBake.span } };
    lampBake = bakeLampLight(lamps);
    kit.lamp.lampVolume.value = lampBake.texture;
    kit.lamp.lampMin.value.copy(lampBake.min);
    kit.lamp.lampSpan.value.copy(lampBake.span);
    noise = buildWeatherNoise();
    weather = createWeather(noise, kit.cloud);
    const deckMaterials = new Set<THREE.MeshStandardMaterial>();
    root.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      for (const mat of (Array.isArray(o.material) ? o.material : [o.material]) as THREE.Material[]) if (mat instanceof THREE.MeshStandardMaterial) deckMaterials.add(mat);
    });
    for (const mat of deckMaterials) {
      addShaderHook(mat, radianceHook(ru));
      if (flags(mat).liftGlow === undefined) addShaderHook(mat, cloudShadowHook(kit.cloud));
      addShaderHook(mat, lampHook(kit.lamp));
    }
    sky = createShipSky(noise.texture);
    root.add(sky.mesh);
    offSky = createOffSky();
    root.add(offSky.mesh);
    const motesSpec = scene.emitters.find((e) => e.kind === 'motes');
    if (motesSpec?.area !== undefined) {
      const a = motesSpec.area;
      const base = motesSpec.base ?? 0;
      motes = createMotes(motesSpec.count, [a.x0, base, -a.y1], [a.x1 - a.x0, (motesSpec.height ?? 0) - base, a.y1 - a.y0]);
      root.add(motes.points);
    }
    motion = createShipMotion({ root, parts, wind: kit.wind, lampLevel: kit.lamp.lampLevel });
    root.add(motion.streaks, motion.smoke);
  }
  const nightGlow = new Set<THREE.MeshStandardMaterial>();
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    for (const mat of (Array.isArray(o.material) ? o.material : [o.material]) as THREE.Material[]) {
      if (mat instanceof THREE.MeshStandardMaterial && flags(mat).nightGlow !== undefined && flags(mat).liftGlow === undefined) nightGlow.add(mat);
    }
  });
  const glazing = m['hullGlazing'] ?? null;
  let lastSun = new THREE.Vector3();
  let hatchActive = false;
  return {
    root,
    kit,
    area,
    bounds,
    merged,
    reveals,
    eggs,
    hatches,
    clutter,
    motes,
    sky,
    motion,
    setAtlas(slot, texture) {
      if (slot === 'clutter') clutter?.setAtlas(texture);
      if (slot === 'hatch') for (const h of hatches.values()) h.setAtlas(texture);
    },
    setEgg(id, state) {
      const e = eggs.get(id);
      if (e === undefined) return;
      e.mesh.visible = state !== 'empty';
      if (state !== 'empty' && e.mesh.material instanceof THREE.MeshStandardMaterial) e.mesh.material.color.set(eggColors[state]);
    },
    frame(f) {
      const l = f.look;
      let shadows = false;
      // Night glow: lantern glass, lit windows, navigation lights (`nightGlow × lamps`).
      for (const mat of nightGlow) mat.emissiveIntensity = (flags(mat).nightGlow ?? 0) * l.lamps;
      if (glazing !== null) {
        glazing.emissive.copy(l.portlight);
        glazing.emissiveIntensity = l.portlightIntensity;
      }
      let sunScale = 1;
      if (deck) {
        // The time of day's wind: idle gusts, and the flight's own wind.
        const g = shipLook.gust;
        const gust = THREE.MathUtils.clamp(g.mean + g.a * Math.sin(f.time * g.aRate) + g.b * Math.sin(f.time * g.bRate + g.bPhase), 0, 1);
        const flight = motion?.blend() ?? 0;
        const wind = (l.wind * (g.low + g.high * gust) + flight * g.flight) * (f.medium ? f.motion : shipLook.wind.offStrength);
        // Cloud shadows: Medium's only (Off is the sun and the hemisphere).
        kit.cloud.csParams.value.x = 0;
        let behind = 1;
        if (weather !== null && f.medium) {
          const sunAtShip = weather.update(f.dt * f.motion, { wind, windDir: l.windDir, cloudShadow: l.cloudShadow, flight, sunDirection: l.sunDirection });
          behind = l.cloudShadow > 0 ? 1 - (1 - sunAtShip) / l.cloudShadow : 1;
          sunScale = shipLook.cloudSun.floor + shipLook.cloudSun.share * behind;
        }
        if (motion !== null) {
          shadows =
            motion.update(f.dt * (f.medium ? f.motion : 0), { wind, lamps: l.lamps, sunColor: l.sunColor, sunAtShip: weather?.sunAtShip() ?? 1, flight: f.flight, dew: l.dew, smoke: l.smoke, snap: !f.medium || f.motion === 0 }) || shadows;
          motion.streaks.visible = f.medium;
          motion.smoke.visible = f.medium;
          // The jetty's clutter rides with the mooring as it falls astern.
          const mooring = parts.get('mooring');
          const first = mooring?.meshes[0];
          clutter?.update(first?.position.x ?? 0, first?.position.y ?? 0, mooring?.fade.value ?? 0);
        }
        if (sky !== null && l.sky !== null) {
          sky.mesh.visible = f.medium;
          if (f.medium) {
            sky.apply(l.sky);
            const d = shipLook.drift;
            const speed = d.base + l.wind * d.wind;
            sky.update(f.dt * f.motion, f.camera, { time: f.time, driftX: l.windDir.x * speed - flight * d.flight, driftZ: l.windDir.y * speed, flight, veil: 1 - behind, attitude: motion?.attitude ?? { roll: 0, pitch: 0, heave: 0 } });
          }
        }
        if (motes !== null) {
          const md = shipLook.moteDrift.base + l.wind;
          motes.update(f.dt * f.motion, { time: f.time, driftX: l.windDir.x * md - flight * shipLook.drift.flight, driftZ: l.windDir.y * md, look: { color: l.moteColor, alpha: l.moteAlpha, radius: l.moteRadius }, viewportHeight: f.viewportHeight, camera: f.camera, visible: f.medium && f.motion > 0 });
        }
      }
      // The roof reveal: a room's roof dissolves while the traveler stands inside its rectangle (a margin keeps it open in a doorway).
      // It eases at both tiers (the view keeps drawing until it settles: `animating`); with reduced motion it snaps.
      const r = shipLook.reveal;
      let revealing = false;
      for (const v of reveals) {
        const inside = Math.abs(f.player.x - v.cx) < v.hx && Math.abs(f.player.z - v.cz) < v.hz;
        v.target = inside ? 1 : 0;
        v.fade.value = f.motion === 0 ? v.target : v.fade.value + (v.target - v.fade.value) * (1 - Math.exp(-f.dt * r.rate));
        if (Math.abs(v.fade.value - v.target) < r.snap) v.fade.value = v.target;
        if (v.fade.value !== v.target) revealing = true;
      }
      // Hatching: the egg in its incubator hides while its sequence plays at the hatch spot (the shell's `playHatch`).
      hatchActive = false;
      for (const h of hatches.values()) {
        const phase: HatchPhase = h.update(f.eventTime, f.camera, f.motion === 0);
        if (phase === 'wobble' || phase === 'crack' || phase === 'glow') hatchActive = true;
      }
      // The sun moved (a time of day blending): the cached shadow map follows.
      if (!lastSun.equals(l.sunDirection)) {
        lastSun = l.sunDirection.clone();
        shadows = true;
      }
      if (f.lens !== null) {
        const u = f.lens.uniforms;
        const e = f.lens.extra;
        const set = (name: string, value: number): void => {
          const slot = e[name];
          if (slot !== undefined) slot.value = value;
        };
        set('airshipIndoor', deck ? 0 : 1);
        set('farMix', l.farMix);
        set('farBlur', l.farBlur);
        (e['farTint']?.value as THREE.Color | undefined)?.copy(l.farTint);
        u.strength.value = shipLook.lens.strength * (f.camera.aspect < shipLook.lens.portraitBelow ? shipLook.lens.portraitFactor : 1);
        u.zoneStrength.value = 0;
        u.hazeAmount.value = 0;
        u.glare.value = 0;
        // The sun's glare (13 Sentinels) only while the sky's sun is on screen, fading behind cloud.
        const sunUv = sky?.sunScreen(f.camera) ?? null;
        if (deck && sunUv !== null && l.grade.glare > 0) {
          const g = shipLook.glare;
          const onScreen = Math.max(0, 1 - Math.max(Math.abs(sunUv.x - 0.5), Math.abs(sunUv.y - 0.5)) * g.reach + g.bias);
          u.sunUv.value.copy(sunUv);
          u.glare.value = l.grade.glare * Math.min(1, onScreen) * (1 - (1 - (weather?.sunAtShip() ?? 1)) * g.cloud);
        }
      }
      if (offSky !== null) {
        offSky.mesh.visible = !f.medium && l.sky !== null;
        if (l.sky !== null && !f.medium) offSky.apply(l.sky.colors.seaFar, l.sky.colors.horizon, l.sky.colors.zenith);
      }
      return { shadows, sunScale, animating: hatchActive || revealing };
    },
    setTier(medium) {
      if (medium) offSky?.release();
      if (!medium) {
        sky?.mesh.geometry.dispose();
        (sky?.mesh.material as THREE.Material | undefined)?.dispose();
        motes?.points.geometry.dispose();
        (motes?.points.material as THREE.Material | undefined)?.dispose();
        motion?.release();
      }
    },
    dispose() {
      root.removeFromParent();
      root.traverse((o) => {
        if (o instanceof THREE.Mesh || o instanceof THREE.Points) (o.geometry as THREE.BufferGeometry).dispose();
      });
      clutter?.dispose();
      for (const h of hatches.values()) h.dispose();
      sky?.dispose();
      offSky?.dispose();
      motes?.dispose();
      motion?.dispose();
      noise?.texture.dispose();
      radianceBake?.texture.dispose();
      lampBake?.texture.dispose();
      for (const o of owned) o.dispose();
      kit.dispose();
    },
  };
};
